/**
 * Роутинг WS `/client/event/form/{coreId}` → FormMessagesOverlay (Swing soft-events).
 *
 * Инициализация: `initFormEventRouter` из GlobalProvider.onMounted.
 */
import type { CoreNotifyEvent } from '@/services/websocket/types'
import { getWebSocketService } from '@/services/websocket/websocket.service'
import { useAppStore } from '@/stores/app'
import { resolveElementRegistryFormId } from '@/common/utils/resolveElementRegistryFormId'
import { readPlatformSessionIdFromPageEntry } from '@/common/utils/platformFileSession'
import {
  mapSoftEventToMessage,
  readFormSoftParamIdentity,
  shouldAutoExecuteFormSoftReload,
  type FormMessageActionType,
  type FormSoftMessage,
} from '@/services/form-events/formSoftEvent'
import {
  addFormSoftMessage,
  clearFormSoftMessages,
  removeFormSoftMessage,
} from '@/services/form-events/formMessagesStore'
import { resolveFormSlotsForCoreEvent } from '@/services/form-events/resolveFormEventSlots'

export type FormEventRouterDeps = {
  reloadForm: (ctx: {
    pageDataSlot: string
    formId?: string
    sessionId?: string
  }) => Promise<unknown> | unknown
  closeForm: (ctx: { pageDataSlot: string; sessionId?: string }) => Promise<unknown> | unknown
}

let routerDeps: FormEventRouterDeps | null = null
let formUnsubscribe: (() => void) | null = null

function unwrapEventData(data: unknown): Record<string, unknown> | undefined {
  if (!data || typeof data !== 'object') return undefined
  const record = data as Record<string, unknown>
  // Already fields map
  if (record._event != null || record['_user.reverseShortName'] != null) {
    return record
  }
  const nested = (record as { fields?: { fields?: Record<string, unknown> } }).fields?.fields
  if (nested && typeof nested === 'object') return nested
  return record
}

export { resolveFormSlotsForCoreEvent }

export function routeFormSoftEvent(event: CoreNotifyEvent): FormSoftMessage[] {
  const coreId = String(event.coreId ?? '').trim()
  if (!coreId) return []

  const data = unwrapEventData(event.data)
  const payloadSessionId =
    (data?._sessionId && typeof data._sessionId === 'object' && data._sessionId !== null
      ? (data._sessionId as { value?: unknown }).value
      : data?._sessionId) ?? null

  const appStore = useAppStore()
  const pagesData = appStore.pagesData as Record<string, unknown>
  const dialogs = (appStore.dialogs || {}) as Record<string, unknown>
  const overlays = (appStore.overlays || {}) as Record<string, unknown>
  const activeSessionId =
    appStore.currentSessionId != null ? String(appStore.currentSessionId).trim() : ''
  const fallbackSlotId =
    appStore.currentPageId != null ? String(appStore.currentPageId).trim() : ''
  const slotIds = resolveFormSlotsForCoreEvent({
    coreId,
    payloadSessionId: payloadSessionId != null ? String(payloadSessionId) : null,
    pagesData,
    dialogs,
    overlays,
    fallbackSlotId: fallbackSlotId || null,
    activeSessionId: activeSessionId || null,
    paramIdentity: readFormSoftParamIdentity(data),
  })

  if (slotIds.length === 0) {
    const knownSessions = [
      ...Object.entries(pagesData).map(([id, e]) => ({
        slot: id,
        session: readPlatformSessionIdFromPageEntry(e),
      })),
      ...Object.entries(dialogs).map(([id, e]) => ({
        slot: id,
        session: readPlatformSessionIdFromPageEntry(e),
      })),
    ].filter((row) => row.session)
    console.warn('[formEventRouter] no form slot for soft event', {
      coreId,
      eventType: event.type,
      payloadSessionId,
      activeSessionId,
      fallbackSlotId,
      knownSessions,
    })
    return []
  }

  const created: FormSoftMessage[] = []
  for (const slotId of slotIds) {
    const pageEntry = pagesData[slotId] ?? dialogs[slotId] ?? overlays[slotId] ?? null
    const formId = pageEntry
      ? resolveElementRegistryFormId(pageEntry as Record<string, any>, slotId)
      : slotId
    const message = mapSoftEventToMessage({
      eventType: event.type,
      data,
      coreId,
      slotId,
      formId,
    })
    if (!message) continue
    if (shouldAutoExecuteFormSoftReload(message)) {
      void executeFormSoftMessageAction(message, 'reload_form')
      created.push(message)
      continue
    }
    addFormSoftMessage(message)
    created.push(message)
  }
  return created
}

export async function executeFormSoftMessageAction(
  message: FormSoftMessage,
  action: FormMessageActionType,
): Promise<void> {
  const deps = routerDeps
  if (!deps) {
    console.warn('[formEventRouter] deps not initialized for action', action)
    return
  }

  if (action === 'hide_message') {
    removeFormSoftMessage(message.slotId, message.id)
    return
  }

  if (action === 'reload_form') {
    try {
      await deps.reloadForm({
        pageDataSlot: message.slotId,
        formId: message.formId,
        sessionId: message.coreId,
      })
      // Форма уже актуальная — стопка «внесены изменения» на этом слоте больше не нужна.
      clearFormSoftMessages(message.slotId)
    } catch (error) {
      console.error('[formEventRouter] reload_form failed', error)
    }
    return
  }

  if (action === 'close_form') {
    try {
      await deps.closeForm({
        pageDataSlot: message.slotId,
        sessionId: message.coreId,
      })
    } catch (error) {
      console.error('[formEventRouter] close_form failed', error)
    } finally {
      removeFormSoftMessage(message.slotId, message.id)
    }
  }
}

function onInboundFormEvent(event: CoreNotifyEvent): void {
  try {
    routeFormSoftEvent(event)
  } catch (error) {
    console.error('[formEventRouter] failed to route form event', error)
  }
}

/** Подключить роутер (один раз, из GlobalProvider.onMounted). */
export function initFormEventRouter(deps: FormEventRouterDeps): () => void {
  routerDeps = deps
  if (!formUnsubscribe) {
    formUnsubscribe = getWebSocketService().onFormEvent(onInboundFormEvent)
  }
  return () => {
    formUnsubscribe?.()
    formUnsubscribe = null
    routerDeps = null
  }
}

/** @internal */
export function __resetFormEventRouterForTests(): void {
  formUnsubscribe?.()
  formUnsubscribe = null
  routerDeps = null
}
